/** @jsx jsx */

import { find, get, isEmpty, map } from 'lodash';

import { Alert, Margin } from '@zapier/design-system';
import { jsx, css } from '@emotion/core';

import FlowForm from 'app/common/components/FlowForm';
import FormaticConfig from 'app/developer-v3/FormaticConfig';

import type { ErrorList } from 'app/developer-v3/types/validator';
import type { TriggerSchema } from 'app/developer-v3/platformSchema/trigger';
import type {
  FormifiedDynamicDropdown,
  InfoMeta,
} from 'app/developer-v3/types';

const Styles = {
  resourceInputs: css`
    margin-top: 20px;
  `,
};

const getFieldNameTemplate = (label: string, choices?: string[]) => ({
  type: choices ? 'pretty-select' : 'single-line-string',
  key: 'name',
  label,
  required: true,
  placeholder: 'ex. id',
  help_text: 'Select the data from this trigger to include in the API call.',
  choices,
});

const getFieldLabelTemplate = (choices?: string[]) => ({
  type: choices ? 'pretty-select' : 'single-line-string',
  key: 'label',
  label: 'Field Label',
  placeholder: 'ex. Name',
  help_text: 'Select the friendly name of the data for users.',
  choices,
});

const getDropdownConfig = (type: string) => services => {
  const baseConfig = {
    type: 'pretty-select',
    key: 'source',
    hasSearch: false,
    choices: map(services, service => ({
      value: service.key,
      label: service.display.label,
    })),
  };

  const configs = {
    search: {
      label: 'Search Source',
      help_text: 'Select the search step with the data for this dropdown.',
      placeholder: isEmpty(services) ? 'No Searches' : 'Select Search',
    },
    dynamic: {
      placeholder: isEmpty(services) ? 'No Triggers' : 'Select Trigger',
    },
  };

  return [{ ...baseConfig, ...configs[type] }];
};

const getActionHandlers = (choices?: string[]) => ({
  help: (
    <Alert status="warning" title="Trigger Needed">
      Add another trigger first to get data from your API for this dropdown.
    </Alert>
  ),
  getDropdownConfig: getDropdownConfig('dynamic'),
  fieldsConfig: [
    getFieldNameTemplate('Field Name', choices),
    getFieldLabelTemplate(choices),
  ],
});

type Props = {
  errors: ErrorList;
  info?: InfoMeta;
  onChange: (options: FormifiedDynamicDropdown) => void;
  triggers: Array<TriggerSchema>;
  value: FormifiedDynamicDropdown;
};

// TODO: Remove this component with SelectTriggerOutputFieldZinnia when all its
// consumers are coverted to Zinnia.
function SelectTriggerOutputField(props: Props) {
  const { errors, info, onChange, triggers, value = {} } = props;

  const source = get(value, ['source'], '');
  const dynamicService = find(triggers, { key: source });
  const outputFields = get(dynamicService, ['operation', 'outputFields'], []);
  const outputFieldKeys = map(outputFields, 'key');
  const actionHandler = getActionHandlers(
    isEmpty(outputFieldKeys) ? undefined : outputFieldKeys
  );

  const onDropdownChange = valueDropdown => {
    onChange(valueDropdown);
  };

  const onTextChange = ({ name, label }) => {
    if (value) {
      onChange({ source: value.source, name, label });
    }
  };

  return (
    <div>
      <FlowForm
        config={FormaticConfig}
        fields={actionHandler.getDropdownConfig(triggers)}
        isValid={true}
        meta={{ errors, info }}
        onChange={onDropdownChange}
        value={value}
      />
      {isEmpty(triggers) ? (
        <Margin margin="20px 0px 0px 0px">{actionHandler.help}</Margin>
      ) : (
        !!source && (
          <FlowForm
            config={FormaticConfig}
            css={Styles.resourceInputs}
            fields={actionHandler.fieldsConfig}
            isValid={true}
            meta={{ errors, info }}
            onChange={onTextChange}
            value={value}
          />
        )
      )}
    </div>
  );
}

export default SelectTriggerOutputField;
